import React, { useState, useEffect } from 'react';
import {
    UserPlus,
    CheckCircle,
    XCircle,
    Clock,
    Loader2,
    MessageSquare
} from 'lucide-react';
import {
    doc,
    collection,
    query,
    where,
    getDocs,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    arrayUnion
} from 'firebase/firestore';
import { db } from '../../../firebase';

const ActivityJoinRequests = ({ activityId, isCreator }) => {
    const [joinRequests, setJoinRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [processing, setProcessing] = useState(null);
    const [showMessage, setShowMessage] = useState(null);

    useEffect(() => {
        // Solo cargar solicitudes si el usuario es el creador
        if (!activityId || !isCreator) {
            setLoading(false);
            return;
        }

        const fetchJoinRequests = async () => {
            try {
                setLoading(true);

                // Buscar solicitudes de esta actividad
                const requestsQuery = query(
                    collection(db, "joinRequests"),
                    where("activityId", "==", activityId),
                    where("status", "==", "pending")
                );

                const querySnapshot = await getDocs(requestsQuery);
                const requests = [];

                // Procesar resultados
                querySnapshot.forEach((doc) => {
                    requests.push({
                        id: doc.id,
                        ...doc.data(),
                        timestamp: doc.data().createdAt?.toDate?.() || new Date()
                    });
                });

                // Ordenar por más recientes primero
                requests.sort((a, b) => b.timestamp - a.timestamp);

                setJoinRequests(requests);
            } catch (err) {
                console.error("Error fetching join requests:", err);
                setError("Couldn't load join requests");
            } finally {
                setLoading(false);
            }
        };

        fetchJoinRequests();
    }, [activityId, isCreator]);

    const handleAccept = async (requestId, userId) => {
        if (processing) return;

        try {
            setProcessing(requestId);

            // 1. Obtener referencia a la actividad
            const activityRef = doc(db, "activities", activityId);

            // 2. Actualizar la lista de asistentes
            await updateDoc(activityRef, {
                attendees: arrayUnion(userId)
            });

            // 3. Actualizar el estado de la solicitud
            const requestRef = doc(db, "joinRequests", requestId);
            await updateDoc(requestRef, {
                status: "accepted",
                processedAt: serverTimestamp()
            });

            // 4. Actualizar UI
            setJoinRequests(prev =>
                prev.filter(request => request.id !== requestId)
            );

            // Opcional: Implementar notificación al usuario

        } catch (err) {
            console.error("Error accepting request:", err);
            setError("Couldn't accept request. Try again.");
        } finally {
            setProcessing(null);
        }
    };

    const handleReject = async (requestId) => {
        if (processing) return;

        try {
            setProcessing(requestId);

            // 1. Actualizar el estado de la solicitud
            const requestRef = doc(db, "joinRequests", requestId);
            await updateDoc(requestRef, {
                status: "rejected",
                processedAt: serverTimestamp()
            });

            // 2. Actualizar UI
            setJoinRequests(prev =>
                prev.filter(request => request.id !== requestId)
            );

            // Opcional: Implementar notificación al usuario

        } catch (err) {
            console.error("Error rejecting request:", err);
            setError("Couldn't reject request. Try again.");
        } finally {
            setProcessing(null);
        }
    };

    const toggleMessage = (requestId) => {
        setShowMessage(showMessage === requestId ? null : requestId);
    };

    // Si el usuario no es el creador o no hay solicitudes, no mostrar nada
    if (!isCreator || (joinRequests.length === 0 && !loading)) {
        return null;
    }

    return (
        <div className="bg-white p-4 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-4">
                <UserPlus className="w-5 h-5 text-blue-500" />
                <span className="font-medium">Join Requests</span>
                {joinRequests.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full ml-2">
                        {joinRequests.length}
                    </span>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                </div>
            ) : error ? (
                <div className="text-center py-4">
                    <p className="text-red-500 text-sm">{error}</p>
                </div>
            ) : joinRequests.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm">
                    <p>No pending requests</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {joinRequests.map((request) => (
                        <div key={request.id} className="border border-gray-100 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                                <img
                                    src={request.userPhoto || "https://via.placeholder.com/40"}
                                    alt={request.userName}
                                    className="w-10 h-10 rounded-full object-cover"
                                />
                                <div className="flex-1">
                                    <p className="font-medium">{request.userName || "Anonymous User"}</p>
                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {new Date(request.timestamp).toLocaleString()}
                                    </p>
                                </div>

                                {/* Message Preview Button */}
                                {request.message && (
                                    <button
                                        className="p-1.5 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                                        onClick={() => toggleMessage(request.id)}
                                    >
                                        <MessageSquare className="w-4 h-4 text-gray-600" />
                                    </button>
                                )}

                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                    <button
                                        className="p-1.5 bg-green-100 rounded-full hover:bg-green-200 transition-colors"
                                        onClick={() => handleAccept(request.id, request.userId)}
                                        disabled={processing === request.id}
                                    >
                                        {processing === request.id ? (
                                            <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
                                        ) : (
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                        )}
                                    </button>
                                    <button
                                        className="p-1.5 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
                                        onClick={() => handleReject(request.id)}
                                        disabled={processing === request.id}
                                    >
                                        {processing === request.id ? (
                                            <Loader2 className="w-5 h-5 text-red-600 animate-spin" />
                                        ) : (
                                            <XCircle className="w-5 h-5 text-red-600" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Message Content */}
                            {showMessage === request.id && request.message && (
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                    <p className="text-xs text-gray-600 mb-1">Message:</p>
                                    <p className="text-sm bg-gray-50 p-2 rounded-lg">{request.message}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ActivityJoinRequests;